// Asset maintenance tasks (EAMM-005 / EAUDITOR-407) - pure helpers shared by the maintenance screens.

export const MAINTENANCE_TASK_TYPE = 'Maintenance'

export const PRIORITY_OPTIONS = [
    { key: 1, value: 1, text: 'Low' },
    { key: 2, value: 2, text: 'Medium' },
    { key: 3, value: 3, text: 'High' },
    { key: 4, value: 4, text: 'Critical' },
]

export const PRIORITY_COLORS = { 1: '#6b7280', 2: '#2563eb', 3: '#d97706', 4: '#dc2626' }

export const LINK_ROLE_LABELS = {
    Work: 'Work checklist',
    Source: 'Source finding / inspection',
    PreInspection: 'Pre-inspection',
    PostInspection: 'Post-inspection (verification)',
}

// How the work is verified (EAUDITOR-410); used while "Verification required" is on.
export const VERIFICATION_METHOD_OPTIONS = [
    { key: 1, value: 1, text: 'Verification inspection' },
    { key: 2, value: 2, text: 'Manager approval' },
    { key: 3, value: 3, text: 'Inspection, then manager approval' },
]

export const needsVerificationInspection = method => Number(method || 1) !== 2
export const needsManagerApproval = method => [2, 3].includes(Number(method || 1))

export const isMaintenanceTask = task =>
    String(task?.TaskType || '').toLowerCase() === MAINTENANCE_TASK_TYPE.toLowerCase()

export const priorityName = id =>
    PRIORITY_OPTIONS.find(p => p.value === Number(id))?.text || 'Medium'

const toPositiveInt = value => {
    const n = Number(value)
    return Number.isInteger(n) && n > 0 ? n : null
}

const trimOrNull = value => {
    const s = (value ?? '').toString().trim()
    return s || null
}

const emptyVerificationSettings = () => ({
    VerificationMethod: 1,
    VerifierUserId: '',
    ApproverUserId: '',
    SeparationOfDuty: false,
    VerificationPassMark: '',
})

export const verificationSettingsFromDto = d => ({
    VerificationMethod: Number(d?.VerificationMethod) || 1,
    VerifierUserId: d?.VerifierUserId || '',
    ApproverUserId: d?.ApproverUserId || '',
    SeparationOfDuty: !!d?.SeparationOfDuty,
    VerificationPassMark: d?.VerificationPassMark ?? '',
})

// { errors, fields } - the server shape (MaintenanceTaskCreateRequest / MaintenanceScheduleSaveRequest)
export const buildVerificationFields = form => {
    const errors = {}
    const method = [1, 2, 3].includes(Number(form.VerificationMethod)) ? Number(form.VerificationMethod) : 1
    const inspection = !!form.VerificationRequired && needsVerificationInspection(method)
    const approval = !!form.VerificationRequired && needsManagerApproval(method)
    let passMark = null
    const raw = (form.VerificationPassMark ?? '').toString().trim()
    if (inspection && raw) {
        const n = Number(raw)
        if (!Number.isFinite(n) || n < 0 || n > 100) errors.VerificationPassMark = '0 to 100 (%)'
        else passMark = n
    }
    if (approval && toPositiveInt(form.ApproverUserId) && toPositiveInt(form.ApproverUserId) === toPositiveInt(form.AssigneeUserId)) {
        errors.ApproverUserId = 'The technician cannot approve their own work'
    }
    if (inspection && form.SeparationOfDuty && toPositiveInt(form.VerifierUserId)
        && toPositiveInt(form.VerifierUserId) === toPositiveInt(form.AssigneeUserId)) {
        errors.VerifierUserId = 'With separation of duty the technician cannot verify'
    }
    return {
        errors,
        fields: {
            VerificationMethod: form.VerificationRequired ? method : 1,
            VerifierUserId: inspection ? toPositiveInt(form.VerifierUserId) : null,
            ApproverUserId: approval ? toPositiveInt(form.ApproverUserId) : null,
            SeparationOfDuty: inspection && !!form.SeparationOfDuty,
            VerificationPassMark: passMark,
        },
    }
}

export const emptyCreateForm = (preset = {}) => ({
    AssetId: preset.AssetId ?? '',
    WorkTemplateId: '',
    AssigneeUserId: '',
    CustomerId: '',
    MaintenanceTypeId: '',
    PriorityId: 2,
    Title: '',
    Instructions: '',
    TaskDue: '',
    VerificationRequired: false,
    CloseOnlyAfterVerification: false,
    VerificationTemplateId: '',
    ...emptyVerificationSettings(),
})

// Returns { errors, payload }; errors is a field -> message map (empty when the form is valid).
export const buildCreatePayload = form => {
    const errors = {}
    const assetId = toPositiveInt(form.AssetId)
    const templateId = toPositiveInt(form.WorkTemplateId)
    if (!assetId) errors.AssetId = 'Select the asset'
    if (!templateId) errors.WorkTemplateId = 'Select the work checklist'
    if (!form.TaskDue) errors.TaskDue = 'Select the due date'
    const title = trimOrNull(form.Title)
    if (title && title.length > 200) errors.Title = 'Title can be at most 200 characters'
    const instructions = trimOrNull(form.Instructions)
    if (instructions && instructions.length > 2000) errors.Instructions = 'Instructions can be at most 2000 characters'
    const verification = buildVerificationFields(form)
    Object.assign(errors, verification.errors)
    const inspection = !!form.VerificationRequired && needsVerificationInspection(verification.fields.VerificationMethod)
    const payload = {
        AssetId: assetId,
        WorkTemplateId: templateId,
        AssigneeUserId: toPositiveInt(form.AssigneeUserId),
        CustomerId: toPositiveInt(form.CustomerId),
        MaintenanceTypeId: toPositiveInt(form.MaintenanceTypeId),
        PriorityId: toPositiveInt(form.PriorityId) || 2,
        Title: title,
        Instructions: instructions,
        TaskDue: form.TaskDue || null,
        VerificationRequired: !!form.VerificationRequired,
        CloseOnlyAfterVerification: !!form.VerificationRequired && !!form.CloseOnlyAfterVerification,
        VerificationTemplateId: inspection ? toPositiveInt(form.VerificationTemplateId) : null,
        ...verification.fields,
    }
    // created from a finding / ticket (EAUDITOR-408): the source links (ids only)
    if (toPositiveInt(form.SourceAuditId)) payload.SourceAuditId = toPositiveInt(form.SourceAuditId)
    if (toPositiveInt(form.SourceQuestionId)) payload.SourceQuestionId = toPositiveInt(form.SourceQuestionId)
    if (toPositiveInt(form.SourceAnswerId)) payload.SourceAnswerId = toPositiveInt(form.SourceAnswerId)
    if (toPositiveInt(form.SourceTicketId)) payload.SourceTicketId = toPositiveInt(form.SourceTicketId)
    return { errors, payload }
}

// Create form fields from GET Maintenance/FromFinding (EAUDITOR-408)
export const formFromFindingPrefill = p => {
    if (!p) return {}
    return {
        Title: p.SuggestedTitle || '',
        Instructions: p.SuggestedInstructions || '',
        PriorityId: Number(p.PriorityId) || 2,
        CustomerId: p.CustomerId || '',
        SourceAuditId: p.AuditId || '',
        SourceQuestionId: p.QuestionId || '',
        SourceAnswerId: p.AnswerId || '',
        SourceTicketId: p.TicketId || '',
    }
}

// PUT body from the detail edit form: only what changed is sent.
export const buildUpdatePayload = (detail, form) => {
    const body = {}
    const typeId = toPositiveInt(form.MaintenanceTypeId)
    const currentType = toPositiveInt(detail?.MaintenanceTypeId)
    if (typeId !== currentType) {
        if (typeId) body.MaintenanceTypeId = typeId
        else body.ClearMaintenanceType = true
    }
    const priority = toPositiveInt(form.PriorityId) || 2
    if (priority !== Number(detail?.PriorityId || 2)) body.PriorityId = priority
    const instructions = (form.Instructions ?? '').toString()
    if (instructions.trim() !== (detail?.Instructions || '').trim()) body.Instructions = instructions.trim()
    if (!!form.VerificationRequired !== !!detail?.VerificationRequired) {
        body.VerificationRequired = !!form.VerificationRequired
    }
    const closeAfter = !!form.VerificationRequired && !!form.CloseOnlyAfterVerification
    if (closeAfter !== !!detail?.CloseOnlyAfterVerification) body.CloseOnlyAfterVerification = closeAfter
    const verificationTemplate = toPositiveInt(form.VerificationTemplateId)
    if (form.VerificationRequired && verificationTemplate && verificationTemplate !== toPositiveInt(detail?.VerificationTemplateId)) {
        body.VerificationTemplateId = verificationTemplate
    }
    // verification settings (EAUDITOR-410): 0 clears verifier / approver, ClearPassMark removes the pass mark
    if (form.VerificationRequired) {
        const { fields } = buildVerificationFields({ ...form, AssigneeUserId: detail?.AssigneeUserId })
        if (fields.VerificationMethod !== (Number(detail?.VerificationMethod) || 1)) body.VerificationMethod = fields.VerificationMethod
        if ('VerifierUserId' in form && (fields.VerifierUserId || null) !== (toPositiveInt(detail?.VerifierUserId) || null)) {
            body.VerifierUserId = fields.VerifierUserId || 0
        }
        if ('ApproverUserId' in form && (fields.ApproverUserId || null) !== (toPositiveInt(detail?.ApproverUserId) || null)) {
            body.ApproverUserId = fields.ApproverUserId || 0
        }
        if (fields.SeparationOfDuty !== !!detail?.SeparationOfDuty) body.SeparationOfDuty = fields.SeparationOfDuty
        const currentMark = detail?.VerificationPassMark ?? null
        if (fields.VerificationPassMark === null && currentMark !== null) body.ClearPassMark = true
        else if (fields.VerificationPassMark !== null && Number(fields.VerificationPassMark) !== Number(currentMark)) {
            body.VerificationPassMark = fields.VerificationPassMark
        }
    }
    return body
}

// Completion card (EAUDITOR-409): form <-> MaintenanceCompletionRequest (the whole card is sent; empty = cleared)
export const completionFormFromDto = c => ({
    WorkPerformed: c?.WorkPerformed || '',
    PartsUsed: c?.PartsUsed || '',
    // numbers as text, like the inputs give them back (the dirty check compares like for like)
    LabourHours: c?.LabourHours != null ? String(c.LabourHours) : '',
    Cost: c?.Cost != null ? String(c.Cost) : '',
    DowntimeHours: c?.DowntimeHours != null ? String(c.DowntimeHours) : '',
    CompletionNotes: c?.CompletionNotes || '',
})

export const buildCompletionPayload = form => {
    const errors = {}
    const amount = (field, max) => {
        const raw = (form[field] ?? '').toString().trim()
        if (!raw) return null
        const n = Number(raw)
        if (!Number.isFinite(n) || n < 0 || n > max) {
            errors[field] = `0 to ${max}`
            return null
        }
        return n
    }
    const payload = {
        WorkPerformed: trimOrNull(form.WorkPerformed),
        PartsUsed: trimOrNull(form.PartsUsed),
        LabourHours: amount('LabourHours', 100000),
        Cost: amount('Cost', 1000000000),
        DowntimeHours: amount('DowntimeHours', 100000),
        CompletionNotes: trimOrNull(form.CompletionNotes),
    }
    return { errors, payload }
}

export const maintenanceApiError = (error, fallback = 'Something went wrong. Please try again.') => {
    const data = error?.error ?? error
    if (typeof data === 'string' && data.trim()) return data
    return data?.Message || data?.message || fallback
}

export const verificationText = detail => {
    if (!detail?.VerificationRequired) return 'Not required'
    const method = detail.VerificationMethodName || 'Verification inspection'
    return detail.CloseOnlyAfterVerification ? `${method} - required before closing` : method
}

// Verification attempt (server Result: 0 Pending, 1 Passed, 2 Failed)
export const verificationResultColor = result => {
    if (result === 1) return 'green'
    if (result === 2) return 'red'
    return 'grey'
}

export const verificationFindingsText = attempt => {
    if (!attempt?.FindingSummary) return ''
    const n = attempt.FindingCount || 0
    return `${n} finding${n === 1 ? '' : 's'}: ${attempt.FindingSummary}`
}

export const formatDate = value => {
    if (!value) return '-'
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return '-'
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

// Create-from-finding warning: tasks already made from this finding (the server also counts ones the user cannot open).
export const existingTasksText = prefill => {
    const listed = Array.isArray(prefill?.ExistingTasks) ? prefill.ExistingTasks : []
    const total = Math.max(Number(prefill?.ExistingTaskCount) || 0, listed.length)
    if (total === 0) return ''
    const names = listed.map(x => `${x.Title} (${x.StageName})`).join(', ')
    const others = total - listed.length
    if (!names) return `${total} maintenance task${total === 1 ? '' : 's'} already made from this finding.`
    return `Already a maintenance task for this finding: ${names}${others > 0 ? ` and ${others} more` : ''}`
}

// "Work done" rows of the verification card (EAUDITOR-410): who did it, when the work checklist was completed, and the
// filled-in completion card fields (empty ones left out).
export const completionSummaryRows = detail => {
    const c = detail?.Completion || {}
    const num = v => (v === null || v === undefined || v === '' ? null : String(v))
    const rows = [
        { label: 'Technician', value: detail?.AssigneeName || '-' },
        {
            label: 'Work completed',
            value: detail?.WorkInspection?.CompletedOn ? formatDate(detail.WorkInspection.CompletedOn) : 'Not yet',
        },
        { label: 'Work performed', value: c.WorkPerformed },
        { label: 'Parts used', value: c.PartsUsed },
        { label: 'Labour hours', value: num(c.LabourHours) },
        { label: 'Cost', value: num(c.Cost) },
        { label: 'Downtime hours', value: num(c.DowntimeHours) },
        { label: 'Notes', value: c.CompletionNotes },
    ]
    return rows.filter(r => r.value !== null && r.value !== undefined && String(r.value).trim() !== '')
}

// Pickable inspections of the asset for pre / post links (need a task that is not itself a maintenance task -
// the server refuses those) and source links (any audit).
export const linkCandidates = (rows, kind, detail, maintenanceTaskIds = []) => {
    const maintenance = new Set((maintenanceTaskIds || []).map(Number))
    const taken = new Set(
        [detail?.WorkInspection, detail?.PreInspection, detail?.PostInspection]
            .filter(Boolean)
            .map(x => x.AuditId),
    )
    return (rows || [])
        .filter(r => !taken.has(r.AuditId))
        .filter(r => kind === 'source' || (toPositiveInt(r.TaskId) && !maintenance.has(Number(r.TaskId))))
        .map(r => ({
            key: r.AuditId,
            value: kind === 'source' ? r.AuditId : r.TaskId,
            text: `${r.AuditName || r.TemplateName || 'Inspection'} - ${formatDate(r.CreatedOn)}${r.CreatedByName ? ` - ${r.CreatedByName}` : ''}`,
        }))
}

// ---------------------------------------------------------------- schedules (EAUDITOR-405 / 406)

export const FREQUENCY_UNIT_OPTIONS = [
    { key: 'D', value: 'D', text: 'Days' },
    { key: 'W', value: 'W', text: 'Weeks' },
    { key: 'M', value: 'M', text: 'Months' },
    { key: 'Y', value: 'Y', text: 'Years' },
]

/** Admins and (department role model) template managers manage schedules - same rule as the server. */
export const canManageMaintenanceSchedules = user => {
    if (!user) return false
    const roleId = Number(user.RoleId ?? user.RoleID)
    if (user.IsSuperAdmin === true || user.IsAdmin === true || roleId === 3 || roleId === 1000) return true
    const role = user?.Capabilities?.Role
    const on = role && (role.RoleModelEnabled === true || String(role.RoleModelEnabled).toLowerCase() === 'true')
    return !!on && (role.CanManageTemplates === true || String(role.CanManageTemplates).toLowerCase() === 'true')
}

const dateOnly = value => (value ? String(value).slice(0, 10) : '')

export const emptyScheduleForm = (preset = {}) => ({
    Name: '',
    MaintenanceTypeId: '',
    CustomerId: '',
    FrequencyUnit: 'M',
    FrequencyInterval: 1,
    StartDate: '',
    NextDueDate: '',
    LeadDays: 0,
    AssigneeUserId: preset.AssigneeUserId ?? '',
    WorkTemplateId: '',
    PriorityId: 2,
    Instructions: '',
    PreInspectionRequired: false,
    PreInspectionTemplateId: '',
    VerificationRequired: false,
    VerificationTemplateId: '',
    CloseOnlyAfterVerification: false,
    ...emptyVerificationSettings(),
})

export const scheduleFormFromDto = d => ({
    Name: d?.Name || '',
    MaintenanceTypeId: d?.MaintenanceTypeId || '',
    CustomerId: d?.CustomerId && Number(d.CustomerId) !== 23 ? d.CustomerId : '',
    FrequencyUnit: d?.FrequencyUnit || 'M',
    FrequencyInterval: d?.FrequencyInterval || 1,
    StartDate: dateOnly(d?.StartDate),
    NextDueDate: dateOnly(d?.NextDueDate),
    LeadDays: d?.LeadDays ?? 0,
    AssigneeUserId: d?.AssigneeUserId || '',
    WorkTemplateId: d?.WorkTemplateId || '',
    PriorityId: d?.PriorityId || 2,
    Instructions: d?.Instructions || '',
    PreInspectionRequired: !!d?.PreInspectionRequired,
    PreInspectionTemplateId: d?.PreInspectionTemplateId || '',
    VerificationRequired: !!d?.VerificationRequired,
    VerificationTemplateId: d?.VerificationTemplateId || '',
    CloseOnlyAfterVerification: !!d?.CloseOnlyAfterVerification,
    ...verificationSettingsFromDto(d),
})

// Returns { errors, payload } for POST / PUT Maintenance/Schedule (MaintenanceScheduleSaveRequest).
export const buildSchedulePayload = (form, assetId) => {
    const errors = {}
    const name = (form.Name || '').trim()
    if (!name) errors.Name = 'Give the schedule a name'
    else if (name.length > 200) errors.Name = 'At most 200 characters'
    const interval = Number(form.FrequencyInterval)
    if (!Number.isInteger(interval) || interval < 1 || interval > 999) errors.FrequencyInterval = '1 to 999'
    if (!['D', 'W', 'M', 'Y'].includes(form.FrequencyUnit)) errors.FrequencyUnit = 'Choose days, weeks, months or years'
    if (!form.StartDate) errors.StartDate = 'Choose the start date'
    if (form.NextDueDate && form.StartDate && form.NextDueDate < form.StartDate) {
        errors.NextDueDate = 'Cannot be before the start date'
    }
    const lead = Number(form.LeadDays)
    if (!Number.isInteger(lead) || lead < 0 || lead > 365) errors.LeadDays = '0 to 365 days'
    if (!toPositiveInt(form.AssigneeUserId)) errors.AssigneeUserId = 'Choose who does the work'
    if (!toPositiveInt(form.WorkTemplateId)) errors.WorkTemplateId = 'Choose the work checklist'
    if (form.PreInspectionRequired && !toPositiveInt(form.PreInspectionTemplateId)) {
        errors.PreInspectionTemplateId = 'Choose the pre-inspection template'
    }
    const verification = buildVerificationFields(form)
    Object.assign(errors, verification.errors)
    const inspection = !!form.VerificationRequired && needsVerificationInspection(verification.fields.VerificationMethod)
    if (inspection && !toPositiveInt(form.VerificationTemplateId)) {
        errors.VerificationTemplateId = 'Choose the verification template'
    }
    const instructions = trimOrNull(form.Instructions)
    if (instructions && instructions.length > 2000) errors.Instructions = 'At most 2000 characters'
    const payload = {
        AssetId: toPositiveInt(assetId),
        Name: name,
        MaintenanceTypeId: toPositiveInt(form.MaintenanceTypeId),
        CustomerId: toPositiveInt(form.CustomerId),
        FrequencyUnit: form.FrequencyUnit,
        FrequencyInterval: interval,
        StartDate: form.StartDate || null,
        NextDueDate: form.NextDueDate || null,
        LeadDays: lead,
        AssigneeUserId: toPositiveInt(form.AssigneeUserId),
        WorkTemplateId: toPositiveInt(form.WorkTemplateId),
        PriorityId: toPositiveInt(form.PriorityId) || 2,
        Instructions: instructions,
        PreInspectionRequired: !!form.PreInspectionRequired,
        PreInspectionTemplateId: form.PreInspectionRequired ? toPositiveInt(form.PreInspectionTemplateId) : null,
        VerificationRequired: !!form.VerificationRequired,
        VerificationTemplateId: inspection ? toPositiveInt(form.VerificationTemplateId) : null,
        CloseOnlyAfterVerification: !!form.VerificationRequired && !!form.CloseOnlyAfterVerification,
        ...verification.fields,
    }
    return { errors, payload }
}
